import { NOTE_EDITOR_TOP_BAR_HEIGHT } from "@/components/note/editor/top-bar/note-editor-top-bar";
import { NoteObservableEditor } from "@/components/note/note-observable-editor";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode, NoteType } from "@/components/note/types";
import { MDSMediaQueries } from "@/mds/foundations";
import { css } from "@/domains/emotion";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { TemplateEditorTopBar } from "@/pages/templates-view/components/template-editor-top-bar";
import { IContactModel } from "@/store/contacts/types";
import { TemplatesViewPageStore } from "@/store/pages/TemplatesViewPageStore";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { useAppStore } from "@/store/utils/hooks";
import { useTheme } from "@emotion/react";
import { HtmlPlaceholderPreset } from "@mem-labs/common-editor";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

interface TemplateEditorProps {
  isSidePanelOpen: boolean;
  goToMention?: TemplatesViewPageStore["goToMention"];
  openFileUploadRejectedModal?: TemplatesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: TemplatesViewPageStore["openImageUploadRejectedModal"];
  templateObservable?: TemplateObservable;
  myAccount?: IContactModel;
}

export const TemplateEditor = observer<TemplateEditorProps>(function TemplateEditor({
  goToMention,
  openFileUploadRejectedModal,
  openImageUploadRejectedModal,
  templateObservable: templateObservable,
  myAccount,
}) {
  const { store } = useAppStore();
  const [editorStore, setEditorStore] = useState<MemCommonEditorStore>();

  const theme = useTheme();
  const editorBackgroundStyles = css({
    background: `linear-gradient(${theme.colors.surface.pane} 30%, ${theme.colors.surface.pane}00), linear-gradient(${theme.colors.surface.pane}00, ${theme.colors.surface.pane} 70%) 0 100%, radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)), radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, .08), rgba(0, 0, 0, 0)) 0 100%`,
    backgroundColor: theme.colors.surface.pane,
  });

  /**
   * Track views of the note editor.
   */
  useEffect(() => {
    if (!templateObservable) return;

    trackEvent(TrackedEvent.TemplateView, {
      note_id: templateObservable.id,
      note_primary_label: templateObservable.title,
    });
  }, [templateObservable, store]);

  if (!templateObservable) return null;

  const htmlPlaceholderPreset = store.spaceAccountFeatureFlags.getFlagStatus("theme-switcher")
    ? HtmlPlaceholderPreset.Default
    : undefined;

  return (
    <div className={fullHeight}>
      <TemplateEditorTopBar template={templateObservable} />
      <NoteObservableEditor
        editorStore={editorStore}
        setEditorStore={setEditorStore}
        noteType={NoteType.Template}
        // Don't steal focus from template name input.
        autoFocus={false}
        className={css(editorStyles, editorBackgroundStyles)}
        goToMention={goToMention}
        openFileUploadRejectedModal={openFileUploadRejectedModal}
        openImageUploadRejectedModal={openImageUploadRejectedModal}
        noteObservable={templateObservable}
        myAccount={myAccount}
        mode={NoteEditorMode.Editable}
        htmlPlaceholderPreset={htmlPlaceholderPreset}
      />
    </div>
  );
});

const fullHeight = css({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
  width: "100%",
});

// Note: left padding is always 16px larger than right padding due to the scrollbar.
// Only issue is on mobile where the scrollbar is overlay it's not part of the width calculations.
const editorStyles = css({
  "--extra-padding-left": `56px`,
  "--extra-padding-right": `56px`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 30px, 100% 30px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
  boxSizing: "border-box",
  cursor: "text",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: `calc(100% - ${NOTE_EDITOR_TOP_BAR_HEIGHT})`,
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  // Consistent sizing whether the scrollbar is visible or not.
  scrollbarGutter: "stable",
  width: "100%",

  "--extra-padding-bottom": "200px",

  [MDSMediaQueries.rules.tablet]: {
    "--extra-padding-left": `40px`,
    "--extra-padding-right": `40px`,
    "--extra-padding-bottom": "100px",
  },

  [MDSMediaQueries.rules.phone]: {
    "--extra-padding-left": `16px`,
    "--extra-padding-right": `16px`,
    "--extra-padding-bottom": "100px",
  },
});
